import { faker } from '@faker-js/faker';
import { environment } from '@environments/environment';
import { ENVIRONMENTS } from '@shared/constants';

import { REAL_PRODUCTS, USE_REAL_BRANDS } from './brands.mock';

export class MockProduct {
  barcode_number: string = faker.number
    .int({
      min: 1000000000000,
      max: 9999999999999,
    })
    .toString();
  label: string = faker.commerce.productName();
}

export const generateProducts: (nbProducts?: number) => MockProduct[] = (
  nbProducts = 30
): MockProduct[] => {
  if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
    const BARCODE_PREFIX = '9900000000';
    return Array.from({ length: nbProducts }, (_, index) => ({
      barcode_number: `${BARCODE_PREFIX}${(index + 1).toString().padStart(3, '0')}`,
      label: `Sample Item ${index + 1}`,
    }));
  }

  return Array.from({ length: nbProducts }, (_, index) => {
    const product = new MockProduct();
    if (USE_REAL_BRANDS) {
      // Demo mode: show real grocery products in the scanner / product list.
      product.label = REAL_PRODUCTS[index % REAL_PRODUCTS.length];
    }
    return product;
  });
};

export interface MockEligibleProduct {
  barcode_number: string;
  label: string;
}

/**
 * Mock for the single-product eligibility endpoint
 * (`GET /offers/:offerId/products?barcode=`). Returns the matching product when
 * the barcode is eligible, or `null` when it isn't — the interceptor turns
 * `null` into a 404, mirroring the real backend.
 *
 * Eligibility is deterministic so both outcomes are reproducible without a real
 * backend: a barcode ending in an even digit is eligible, an odd one isn't
 * (the task's example, `3354599572002`, ends in an even digit and is eligible).
 */
export const generateEligibleProduct = (
  barcode: string
): MockEligibleProduct | null => {
  const trimmed: string = barcode.trim();
  const lastDigit: number = Number(trimmed.at(-1));
  const isEligible: boolean =
    trimmed.length > 0 && !Number.isNaN(lastDigit) && lastDigit % 2 === 0;

  if (!isEligible) {
    return null;
  }

  if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
    return { barcode_number: trimmed, label: `Sample Item ${trimmed}` };
  }

  return { barcode_number: trimmed, label: faker.commerce.productName() };
};
